import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { loginApi } from "apis/eduidLogin";
import { EduIDButton } from "components/Common/EduIDButton";
import { ResponseCodeButtons } from "components/Common/ResponseCodeAbortButton";
import { TimeRemainingWrapper } from "components/Common/TimeRemaining";
import { useAppDispatch, useAppSelector } from "eduid-hooks";
import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import loginSlice from "slices/Login";
import { ExpiresMeter } from "./ExpiresMeter";
import { LoginAtServiceInfo } from "./LoginAtServiceInfo";
import { ResponseCodeForm } from "./ResponseCodeForm";
/*
 * Start the "Login using another device" login flow.
 *
 * This component runs on device #1 and shows the QR code to the user. It then waits
 * for the user to submit a "response code" in a form, to complete the login on this device.
 */
export function UseOtherDevice1() {
    const loginRef = useAppSelector((state) => state.login.ref);
    const other_device = useAppSelector((state) => state.login.other_device1);
    const remember_me = useAppSelector((state) => state.login.remember_me);
    const service_info = useAppSelector((state) => state.login.service_info);
    const this_device = useAppSelector((state) => state.login.this_device);
    const username = useAppSelector((state) => state.login.authn_options.forced_username);
    const [fetchUseOtherDevice1] = loginApi.useLazyFetchUseOtherDevice1Query();
    useEffect(() => {
        if (loginRef && !other_device) {
            // refresh state on page reload
            const _name = username || undefined; // backend is picky and won't allow null
            fetchUseOtherDevice1({ ref: loginRef, action: "FETCH", username: _name, this_device, remember_me });
        }
    }, [fetchUseOtherDevice1, loginRef, other_device, remember_me, this_device, username]);
    const hasQrCode = other_device &&
        (other_device.state === "NEW" || other_device.state === "IN_PROGRESS" || other_device.state === "AUTHENTICATED");
    let error = undefined;
    if (!hasQrCode && other_device) {
        if (other_device.state === "ABORTED") {
            error = (_jsx(FormattedMessage, { id: "useOtherDevice1.aborted", defaultMessage: "The request has been aborted", description: "Use other device" }));
        }
        else {
            error = (_jsx(FormattedMessage, { id: "useOtherDevice1.invalid", defaultMessage: "The request is not valid anymore", description: "Use other device" }));
        }
    }
    return (_jsxs("div", { className: "use-another-device device1", children: [_jsx("h1", { children: _jsx(FormattedMessage, { id: "useOtherDevice1.logUsing", defaultMessage: "Log in using another device" }) }), _jsx("div", { className: "lead", children: _jsx(LoginAtServiceInfo, { service_info: service_info }) }), !error && hasQrCode && _jsx(RenderOtherDevice1, { data: other_device }), error && _jsx(RenderFatalError, { error: error })] }));
}
// Render a fatal error message with a CANCEL button that will reset the use-other-device
function RenderFatalError({ error, handleNewQRCodeOnClick, }) {
    const dispatch = useAppDispatch();
    function handleCancelButtonOnClick() {
        // Exit from "use another device"
        dispatch(loginSlice.actions.stopLoginWithAnotherDevice());
    }
    return (_jsxs(_Fragment, { children: [_jsx("p", { children: error }), _jsxs("div", { className: "buttons", children: [_jsx(EduIDButton, { buttonstyle: "secondary", type: "button", onClick: handleCancelButtonOnClick, id: "response-code-cancel-button", children: _jsx(FormattedMessage, { id: "common.cancel", defaultMessage: "Cancel", description: "button cancel" }) }), handleNewQRCodeOnClick && (_jsx(EduIDButton, { buttonstyle: "primary", type: "button", id: "refresh-get-new-code", onClick: handleNewQRCodeOnClick, children: _jsx(FormattedMessage, { id: "common.retry", defaultMessage: "Retry", description: "Login OtherDevice" }) }))] })] }));
}
function RenderOtherDevice1({ data }) {
    const login_ref = useAppSelector((state) => state.login.ref);
    const username = useAppSelector((state) => state.login.authn_options.forced_username);
    const this_device = useAppSelector((state) => state.login.this_device);
    const remember_me = useAppSelector((state) => state.login.remember_me);
    const response_code_required = useAppSelector((state) => { var _a; return (_a = state.login.other_device1) === null || _a === void 0 ? void 0 : _a.response_code_required; });
    const bad_attempts = useAppSelector((state) => { var _a; return (_a = state.login.other_device1) === null || _a === void 0 ? void 0 : _a.bad_attempts; });
    const [isExpired, setIsExpired] = useState(false);
    const [fetchUseOtherDevice1] = loginApi.useLazyFetchUseOtherDevice1Query();
    function handleTimerReachZero() {
        setIsExpired(true);
    }
    function handleAbortButtonOnClick(event) {
        event === null || event === void 0 ? void 0 : event.preventDefault();
        if (login_ref) {
            // Tell backend we're abandoning the request to login using another device
            fetchUseOtherDevice1({ ref: login_ref, action: "ABORT" });
        }
    }
    function handleNewQRCodeOnClick() {
        // Get new code
        if (login_ref) {
            const _name = username || undefined; // backend is picky and won't allow null
            fetchUseOtherDevice1({ ref: login_ref, action: "FETCH", username: _name, this_device, remember_me });
            setIsExpired(false);
        }
    }
    function handleSubmitCode(values) {
        const code = values.v.join("");
        if (/^\d{6}$/.test(code) && login_ref) {
            fetchUseOtherDevice1({
                ref: login_ref,
                action: "SUBMIT_CODE",
                response_code: code,
                this_device,
                remember_me,
            });
        }
    }
    function handleContinueWithoutCode() {
        // If the user is known on device #1, the correct response code is not required by the backend
        if (login_ref) {
            fetchUseOtherDevice1({
                ref: login_ref,
                action: "SUBMIT_CODE",
                response_code: "000000",
                this_device,
                remember_me,
            });
        }
        return undefined;
    }
    const expiredMessage = (_jsx(FormattedMessage, { id: "useOtherDevice1.another1", defaultMessage: "The login attempt was aborted or exceeded the time limit. Please try again.", description: "Use another device #1" }));
    return (_jsxs(_Fragment, { children: [isExpired ? (_jsx(RenderFatalError, { error: expiredMessage, handleNewQRCodeOnClick: handleNewQRCodeOnClick })) : (_jsxs("ol", { className: "listed-steps", children: [_jsxs("li", { children: [_jsx(FormattedMessage, { id: "useOtherDevice1.scan", defaultMessage: "Scan this QR-code with your other device" }), _jsxs("figure", { className: "x-adjust", children: [_jsx("img", { alt: "qr-code", className: "qr-code", src: data.qr_img }), _jsxs("figcaption", { className: "short-code", children: ["ID# ", data.short_code] })] })] }), _jsx("li", { children: _jsx(FormattedMessage, { id: "useOtherDevice1.log", defaultMessage: "Log in on the other device" }) }), _jsx("li", { children: response_code_required ? (_jsxs(_Fragment, { children: [_jsx(FormattedMessage, { id: "useOtherDevice1.enter", defaultMessage: "Enter the six digit response code shown on the other device in the form below" }), _jsxs("div", { className: "expiration-info", children: [_jsx(ResponseCodeForm, { bad_attempts: bad_attempts, inputsDisabled: false, handleSubmitCode: handleSubmitCode, children: _jsx(ResponseCodeButtons, { handleAbortButtonOnClick: handleAbortButtonOnClick }) }), _jsx(TimeRemainingWrapper, { name: "other-device-expires", unique_id: data.display_id, value: data.expires_in, onReachZero: handleTimerReachZero, children: _jsx(ExpiresMeter, { showMeter: false, expires_max: data.expires_max }) })] })] })) : (_jsxs(_Fragment, { children: [_jsx(FormattedMessage, { id: "useOtherDevice1.click", defaultMessage: `Click "continue" once you have logged in on the other device` }), _jsxs("div", { className: "buttons", children: [_jsx(EduIDButton, { buttonstyle: "secondary", type: "button", onClick: handleAbortButtonOnClick, id: "response-code-cancel-button", children: _jsx(FormattedMessage, { id: "common.cancel", defaultMessage: "Cancel", description: "button cancel" }) }), _jsx(EduIDButton, { type: "button", buttonstyle: "primary", onClick: handleContinueWithoutCode, id: "response-code-continue-button", children: _jsx(FormattedMessage, { id: "common.continue", defaultMessage: "Continue", description: "Login OtherDevice" }) })] })] })) })] })), _jsx(DeveloperInfo, Object.assign({}, data))] }));
}
function DeveloperInfo({ qr_url }) {
    const env = useAppSelector((state) => state.config.environment);
    if (!qr_url || (env !== "dev" && env !== "staging")) {
        return null;
    }
    return (_jsxs("div", { className: "developer", children: [_jsx("p", {}), _jsx("span", { children: _jsx(FormattedMessage, { id: "useOtherDevice2.info", defaultMessage: "Developer info, not shown in production:" }) }), _jsx("span", { id: "qr_url", children: _jsx("a", { href: qr_url, children: qr_url }) })] }));
}
